import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import MainActions from "@/components/MainActions";
import Breadcrumbs from "@/components/Breadcrumbs";
import VLFCard from "@/components/Card";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFDatePicker from "@/components/Input/DatePicker";
import Button from "@mui/material/Button";
import CardsContainer from "@/components/CardsContainer";
import CardsTable from "@/components/CardsTable";
import HandlePlayersDataModal from "@/pages/AnthropometricData/handlePlayersData";

import {PlayerAPI} from "@/api/player";
import {RosterAPI} from "@/api/roster";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	tqrOptions,
} from "@/utils/constants";
import {useCategories, usePlayers} from "@/utils/hooks";
import {checkRequiredFields} from "@/utils/functions";

function HandleTQRPage(props) {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();

	const seasonId = useSelector((state) => state?.season?.id);
	const playerId = useSelector((state) => state.user?.playerId);
	const userRole = useSelector((state) => state?.user?.roleName);
	const isPlayer = userRole === "Player";

	const [internalTeams, setInternalTeams] = useState([]);
	const getTeams = async () => {
		try {
			const response = await RosterAPI.list({seasonId, prospect: false});
			setInternalTeams(response);
		} catch (error) {
			console.log(error);
		}
	};
	useEffect(() => {
		if (seasonId) {
			getTeams();
		}
	}, [seasonId]);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		formatted: true,
		onlyPro: true,
	});
	const {players} = usePlayers({
		setLoading: false,
		handleNotification: undefined,
		goalkeepers: false,
		onlyInternal: true,
		prospect: false,
	});

	const [loading, setLoading] = useState(false);
	const [fields, setFields] = useState({});

	const [openModal, setOpenModal] = useState(false);
	const [initialData, setInitialData] = useState();
	const [playersList, setPlayersList] = useState([]);

	const getRequiredFields = () => {
		return ["tqrDate", "tqrValue"];
	};

	const saveTQR = async () => {
		setLoading(true);
		try {
			isPlayer
				? await PlayerAPI.createTQR({
						id,
						playerId,
						tqrDate: moment(fields.tqrDate).format("YYYY-MM-DD"),
						tqrValue: fields.tqrValue?.value,
					})
				: await PlayerAPI.createRecoveries(
						playersList.map((p) => {
							return {
								playerId: p.player.value,
								tqrValue: p.tqr.value,
								tqrDate: moment(fields.tqrDate).format("YYYY-MM-DD"),
							};
						}),
					);
			navigate("/measurations/tqr");
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const columns = [
		{
			label: "Giocatore",
			key: "player",
			customRender: (item) => {
				return item.player?.label;
			},
		},
		{
			label: "TQR",
			key: "tqr",
			customRender: (item) => {
				return item.tqr?.label;
			},
		},
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/measurations/tqr"),
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: saveTQR,
			disabled: isPlayer
				? checkRequiredFields(getRequiredFields(), fields).length
					? true
					: false
				: !fields.tqrDate || !playersList.length,
		},
	];

	const breadcrumbsElements = [{label: "TQR", path: "/measurations/tqr"}];

	const render = isPlayer ? (
		<VLFCard
			title="Informazioni principali"
			openable={false}
			content={
				<div className="row">
					<div className="col-sm-12 col-md-6">
						<VLFDatePicker
							label="Data"
							value={fields.tqrDate}
							onChange={(value) => {
								setFields({...fields, tqrDate: value});
							}}
							required
							size="small"
						/>
					</div>
					<div className="col-sm-12 col-md-6">
						<VLFSelectNew
							options={tqrOptions}
							value={fields.tqrValue}
							onChange={(value) => {
								setFields({...fields, tqrValue: value});
							}}
							required
							label="TQR"
							sorted={false}
						/>
					</div>
				</div>
			}
		/>
	) : (
		<div className="d-flex flex-column gap-2">
			<VLFCard
				openable={false}
				content={
					<div className="row no-margin">
						<div className="col-12 col-md-4 padding-small">
							<VLFDatePicker
								label="Data"
								value={fields.tqrDate}
								onChange={(value) => {
									setFields({...fields, tqrDate: value});
								}}
								required
								size="small"
							/>
						</div>
					</div>
				}
			/>
			<VLFCard
				title="Recuperi"
				openable={false}
				content={
					<>
						<div className="d-flex align-items-center justify-content-end">
							<Button
								variant="contained"
								onClick={() => setOpenModal(true)}
								size="small"
							>
								<div className="d-flex align-items-center">
									<AddIcon />
								</div>
							</Button>
						</div>
						<CardsContainer
							tableComponent={
								<CardsTable
									fullHeight={false}
									configurationColumns={columns}
									data={playersList}
									rowActions
									handleEdit={(item) => {
										setInitialData(Object.assign({}, item));
										setOpenModal(true);
									}}
									handleDelete={(item) => {
										const updatedList = Array.from(playersList);
										const index = updatedList.findIndex(
											(element) => element.player.value === item.player.value,
										);
										updatedList.splice(index, 1);
										setPlayersList(updatedList);
									}}
								/>
							}
							listComponent={<></>}
						/>
					</>
				}
			/>
			{openModal ? (
				<HandlePlayersDataModal
					type="tqr"
					open={openModal}
					confirm={(values) => {
						if (initialData) {
							const updatedList = Array.from(playersList);
							const index = updatedList.findIndex(
								(item) => item.player.value === values.player.value,
							);
							updatedList[index] = {
								...updatedList[index],
								...values,
							};
							setPlayersList(updatedList);
						} else {
							if (values.player) {
								setPlayersList([...playersList, values]);
							}
							if (values.players) {
								const multiplePlayers = [];
								Object.keys(values.players)?.forEach((pk) => {
									if (values.players[pk].tqr) {
										multiplePlayers.push(values.players[pk]);
									}
								});
								setPlayersList([...playersList, ...multiplePlayers]);
							}
						}
						setOpenModal(false);
						setInitialData(undefined);
					}}
					close={() => {
						setOpenModal(false);
						setInitialData(undefined);
					}}
					initialData={initialData}
					categories={categories}
					teams={internalTeams}
					players={players?.filter((p) => {
						const isInList = playersList?.filter(
							(pl) => pl.player.value === p.id,
						)?.[0];
						return !isInList;
					})}
				/>
			) : null}
		</div>
	);

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="row no-margin">
				<div className="col-sm-12 padding-small">{render}</div>
			</div>
		</Page>
	);
}

export default HandleTQRPage;
